import { Fragment as _Fragment, jsx as _jsx } from "react/jsx-runtime";
/**
 * Conditional rendering, and a standing reminder that it is not a permission check.
 *
 * Invariant 7: "a hidden button is not a permission check". The server re-runs
 * `require_feature` on every gated route, and it is the only thing standing between a
 * caller and a capability. This component exists so a person on the essential tier is not
 * shown a Reserve button that always 403s — a usability decision, not a security one.
 *
 * Two consequences the screens in this package actually observe:
 *
 *   1. Every mutation still renders `FeatureNotInPlanError` on its error path. Entitlements
 *      are cached for five minutes; a plan can change inside that window.
 *   2. `mode="disable"` is preferred over hiding for a capability a tenant could plausibly
 *      buy, because a control that is absent teaches nothing and a control that says why
 *      it is unavailable sells the upgrade.
 */
import { Box, Tooltip } from '@mui/material';
import { useFeature } from '@inai/inventory-core/react';
export function FeatureGate({ feature, children, mode = 'hide', fallback = null, reason, }) {
    const { enabled, loading, suspended } = useFeature(feature);
    // While entitlements are loading, render nothing rather than flashing a control that is
    // about to disappear.
    if (loading)
        return null;
    if (enabled)
        return _jsx(_Fragment, { children: children });
    if (mode === 'hide')
        return _jsx(_Fragment, { children: fallback });
    const message = suspended
        ? 'This organization is suspended, so every inventory capability is unavailable until billing is current.'
        : (reason ?? `Your plan does not include ${String(feature).replace(/_/g, ' ')}.`);
    return (_jsx(Tooltip, { title: message, children: _jsx(Box, { component: "span", sx: { opacity: 0.45, pointerEvents: 'none', display: 'inline-flex' }, children: children }) }));
}
//# sourceMappingURL=FeatureGate.js.map